import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { ReactNode, useState } from 'react';
import { AuthProvider, hasAuthParams } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';
import { useEffectOnce, useMount } from 'react-use';

import { clientConstants } from '@bedc/client/constants';

import { useUserAuth } from '../hooks/useUserAuth';
import {
	createAuthSessionKey,
	getIsLoggingOut,
	getOidcAuthConfig,
	getSafeRedirectPath,
	refreshSession,
	readLegacyStoredSession,
	setOidcUserManager,
} from '../utils/userAuthUtils';

export const OidcAuthProvider = ({ children }: { children: ReactNode }) => {
	// Hooks
	// ------------------------------------------------------------------------

	/** UserManager instance. */
	const [userManager, setUserManager] = useState<UserManager | null>(null);

	/** OIDC auth config. */
	const [authConfig, setAuthConfig] = useState<UserManagerSettings | null>(null);

	// Lifecycle
	// ------------------------------------------------------------------------

	/** Initialize OIDC auth config and the global user manager. */
	useMount(() => {
		readLegacyStoredSession();

		const oidcAuthConfig = getOidcAuthConfig();
		const oidcUserManager = new UserManager(oidcAuthConfig);
		setAuthConfig(oidcAuthConfig);
		setUserManager(oidcUserManager);
		// Set the OIDC UserManager instance globally for use in non-React contexts
		setOidcUserManager(oidcUserManager);
	});

	// Template
	// ------------------------------------------------------------------------

	if (!userManager || !authConfig) {
		return null;
	}

	return (
		<AuthProvider
			{...authConfig}
			onSigninCallback={() =>
				// Needed for signInSilent to work behind the scenes to refresh the token.
				// See https://github.com/authts/react-oidc-context/blob/f175dcba6ab09871b027d6a2f2224a17712b67c5/src/AuthProvider.tsx#L20-L30
				window.history.replaceState({}, document.title, window.location.pathname)
			}
			userManager={userManager}
		>
			<OidcAuthenticator>{children}</OidcAuthenticator>
		</AuthProvider>
	);
};

let isAuthenticating = false;

/**
 * Returns the redirect stored when the user pressed Login, tied to the sign-in by its session ID.
 *
 * @param user - The signed-in user.
 * @returns The stored path, or `null` when there is none.
 */
const takeStoredRedirect = (user: User): string | null => {
	const sessionId =
		user.state &&
		typeof user.state === 'object' &&
		'sessionId' in user.state &&
		typeof user.state.sessionId === 'string'
			? user.state.sessionId
			: null;

	if (!sessionId) {
		return null;
	}

	const sessionKey = createAuthSessionKey(sessionId);
	const redirectTo = sessionStorage.getItem(sessionKey);
	sessionStorage.removeItem(sessionKey);

	return getSafeRedirectPath(redirectTo);
};

const OidcAuthenticator = ({ children }: { children: ReactNode }) => {
	// Hooks
	// ------------------------------------------------------------------------

	/** User auth hook. */
	const { isLoading, isAuthenticated, user, activeNavigator } = useUserAuth();

	/** Location hook to access the current URL. */
	const location = useLocation();

	/**
	 * The path the edge sent the user from. Read on the first render, before `LoginPage` removes
	 * the parameter from the URL, so a resumed session can return there too.
	 */
	const [requestedPath] = useState(() =>
		getSafeRedirectPath(
			new URLSearchParams(window.location.search).get(clientConstants.auth.RedirectToParam)
		)
	);

	// Properties
	// ------------------------------------------------------------------------

	const isLoginPage = location.pathname === clientConstants.auth.LoginPath;

	const currentPath = `${location.pathname}${location.search}${location.hash}`;

	/** Sign in silently when the user is not authenticated, outside the login page. */
	const shouldResumeSession =
		!isLoading &&
		!isAuthenticated &&
		!getIsLoggingOut() &&
		!hasAuthParams() &&
		!activeNavigator &&
		!isLoginPage;

	/**
	 * The signed-in user lives in memory, so every page load starts without one and restores it
	 * from the refresh cookie. Hold the app back until then, so it never runs signed out.
	 */
	const isWaitingForSession = !isAuthenticated && !isLoginPage;

	// Methods
	// ------------------------------------------------------------------------

	/** Leave the login page for the intended page once signed in. */
	const redirectAfterLogin = () => {
		if (!user) {
			return;
		}

		// The session endpoint that signed the user in has already written the edge cookie the
		// protected route needs.
		window.location.href = takeStoredRedirect(user) ?? requestedPath ?? '/';
	};

	/** Refresh the session, or send the user to the login page when it cannot be refreshed. */
	const resumeSession = () => {
		if (isAuthenticating) {
			return;
		}

		isAuthenticating = true;
		refreshSession().then((authed) => {
			isAuthenticating = false;
			if (!authed) {
				window.location.href = `${clientConstants.auth.LoginPath}${currentPath && currentPath !== '/' ? `?${clientConstants.auth.RedirectToParam}=${encodeURIComponent(currentPath)}` : ''}`;
			}
		});
	};

	// Template
	// ------------------------------------------------------------------------

	return (
		<>
			{isAuthenticated && user && isLoginPage && (
				<AuthSyncLayer
					key={`login-redirect:${user.access_token}`}
					run={redirectAfterLogin}
				/>
			)}
			{shouldResumeSession && (
				<AuthSyncLayer key={`resume-session:${currentPath}`} run={resumeSession} />
			)}
			{/* Wait until authenticated before continuing loading the app. */}
			{isLoading || isWaitingForSession ? null : children}
		</>
	);
};

/** Runs one auth sync post-commit. Remounted by its key on each change. */
const AuthSyncLayer = ({ run }: { run: () => void }): null => {
	useEffectOnce(run);

	return null;
};
